import homeApi from "@features/home/helpers/homeApi";
import useDebugMode from "@shared/hooks/useDebugMode";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { getGuestSessionPayload } from "@utils/getGuestSessionPayload";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  BannerData,
  FestivalObjStaticDataDt,
  IGuestSessionInput,
  StatisDataDt,
  UserDataDt,
  UserSubscriptionDataDT,
} from "types/api/layout";

const GUEST_SESSION_ID_CACHE_KEY = "guest_session_id";

type LayoutContextType = {
  staticData: FestivalObjStaticDataDt | null;
  userData: UserDataDt | null;
  isAdmin: boolean;
  isUserDataLoading: boolean;

  refetchStatic: () => void;
  refetchUser: () => void;
  refetchGuestSession: () => void;

  subscriptionData: UserSubscriptionDataDT | null;
  isSubscriptionDataLoading: boolean;
  fetstivalBanner: BannerData | null;
  guestSessionId: string | null;
};

export interface LayoutStateDT {
  staticData: FestivalObjStaticDataDt | null;
  userData: UserDataDt | null;
  isAdmin: boolean;
  isUserDataLoading: boolean;
  subscriptionData: UserSubscriptionDataDT | null;
  isSubscriptionDataLoading: boolean;
  fetstivalBanner: BannerData | null;
  guestSessionId: string | null;
}

const formatStaticData = (
  staticData?: StatisDataDt
): FestivalObjStaticDataDt | null => {
  if (!staticData) return null;
  const theFestival = staticData?.festival?.find((fes) => fes.is_active);
  return { ...staticData, festival: theFestival || null };
};

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);
LayoutContext.displayName = "LayoutContext";

const LayoutProvider: FC<{ children?: ReactNode; lite?: boolean }> = ({
  children,
  lite = false,
}) => {
  const { data: staticData, mutate: refetchStatic } = useRequestImmutable(
    lite ? null : homeApi.getStaticLayout()
  );
  const {
    data: userLayoutData,
    isLoading: isUserDataLoading,
    mutate: refetchUser,
  } = useRequestImmutable(homeApi.getUserLayoutData());

  const { data: festivalBannerData, mutate: refetchBanner } =
    useRequestImmutable(lite ? null : homeApi.getFestivalBanner());

  const isUserLoggedIn = Boolean(userLayoutData?.data?.is_login);
  const { data: subscriptionData, isLoading: isSubscriptionRequestLoading } =
    useRequestImmutable(
      Boolean(!lite && isUserLoggedIn)
        ? homeApi.getUserSubscriptionData()
        : undefined
    );

  const guestData = useMemo(() => {
    const canFetch =
      Boolean(userLayoutData?.data) &&
      !userLayoutData?.data?.is_login &&
      typeof window !== "undefined";

    const cachedValue =
      typeof window !== "undefined"
        ? localStorage.getItem(GUEST_SESSION_ID_CACHE_KEY)
        : null;

    const payload = canFetch ? getGuestSessionPayload() : null;

    return {
      shouldFetch: canFetch && payload && !cachedValue,
      payload,
      cache: typeof cachedValue === "string" ? cachedValue : null,
    };
  }, [userLayoutData]);

  const { data: guestSessionData, mutate: refetchGuestSession } =
    useRequestImmutable(
      guestData.shouldFetch
        ? homeApi.postToGetGuestSession(guestData.payload as IGuestSessionInput)
        : undefined
    );

  const layoutState = useMemo<LayoutStateDT>(() => {
    const fetchedSessionId = guestSessionData?.id;

    if (fetchedSessionId && !guestData?.cache) {
      const isValidValue = typeof fetchedSessionId === "string";

      if (isValidValue)
        localStorage.setItem(GUEST_SESSION_ID_CACHE_KEY, fetchedSessionId);
    }

    return {
      staticData: formatStaticData(staticData?.data),
      userData: userLayoutData?.data ?? null,
      isAdmin: Boolean(userLayoutData?.data?.user?.isAdmin),
      isUserDataLoading,
      subscriptionData: subscriptionData?.data ?? null,
      isSubscriptionDataLoading:
        isUserDataLoading || (isUserLoggedIn && isSubscriptionRequestLoading),
      fetstivalBanner: festivalBannerData?.data ?? null,
      guestSessionId: guestData?.cache ?? fetchedSessionId ?? null,
    };
  }, [
    guestData?.cache,
    festivalBannerData?.data,
    guestSessionData?.id,
    isSubscriptionRequestLoading,
    isUserDataLoading,
    isUserLoggedIn,
    staticData?.data,
    subscriptionData?.data,
    userLayoutData?.data,
  ]);

  useDebugMode(layoutState);

  const refetchUserHandler = useCallback(() => {
    void Promise.all([refetchUser(), refetchBanner()]);
  }, [refetchBanner, refetchUser]);

  const contextValue = useMemo<LayoutContextType>(
    () => ({
      ...layoutState,
      refetchUser: refetchUserHandler,
      refetchStatic,
      refetchGuestSession,
    }),
    [layoutState, refetchGuestSession, refetchStatic, refetchUserHandler]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

export function useLayoutContext() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error(`useLayoutContext must be used within a LayoutProvider`);
  }
  return context;
}

export default LayoutProvider;
